import type { MeasureBy } from '@obraplan/core/domain/takeoff/geometry';

/**
 * Número em português, nas duas direções.
 *
 * Vive separado dos campos porque é usado por quem só lê — a tabela do orçamento, o painel
 * de áreas — e porque `.ts` e `.tsx` no mesmo arquivo custam o recarregamento rápido do
 * Vite, que é o que mantém a montagem em andamento enquanto se mexe na tela.
 */

/** `1250.5` vira `"1.250,50"` — para ler, nunca para voltar a número. */
export function metros(value: number, casas = 2): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

/**
 * `1.23456` vira `"R$ 1,2346"`, e `12.5` vira `"R$ 12,50"` — o preço **unitário**, com até
 * quatro casas (Fase 29, D17).
 *
 * Não é o `Money.format`: o `Money` guarda centavos inteiros (ADR-106), que é o certo para o
 * total, e o preço de um prego ou de um quilo de aço vive abaixo do centavo — arredondado a
 * duas casas, vezes dez mil pregos, vira reais que ninguém pagou. Duas casas no mínimo, como
 * se lê dinheiro; até quatro quando o preço as tem.
 */
export function precoUnitario(value: number): string {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/**
 * O número que o desenho determina, na precisão que a grandeza pede (Fase 24, fatia 10).
 *
 * A contagem é de peças inteiras: "2", e não "2,00" — uma janela não tem casas decimais. Mas a
 * divisa reparte a peça entre dois aptos, e meia parede não pode virar "1" calada: sobrando
 * fração, ela aparece. Metro, m² e m³ levam as duas casas de sempre (o volume pede três, e
 * quem o mostra diz).
 */
export function numeroDaMedida(value: number, measureBy: MeasureBy): string {
  if (measureBy !== 'COUNT') return metros(value);
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** `"faces (4 × altura), m²"` vira `"Faces (4 × altura), m²"` — a mesma frase, no começo de uma célula. */
export function primeiraMaiuscula(texto: string): string {
  return texto.charAt(0).toLocaleUpperCase('pt-BR') + texto.slice(1);
}

/** `3.5` vira `"3,5"` — sem separador de milhar, para poder voltar a número intacto. */
export function paraTexto(value: number): string {
  return String(value).replace('.', ',');
}

/** `"3,5"` e `"3.5"` viram `3.5`; `"3,"` e `""` não viram nada — ainda estão sendo digitados. */
export function paraNumero(texto: string): number | undefined {
  const limpo = texto.replace(/\s/g, '').replace(',', '.');
  if (limpo === '' || limpo.endsWith('.')) return undefined;
  const valor = Number(limpo);
  return Number.isFinite(valor) ? valor : undefined;
}
